import React, { useState, useRef, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import TauriImage from "../../../TauriImage/TauriImage";
import "./TauriCalendar.css";
import { activityClassification, editableWorksheetStatuses, eventTypes, planWorksheetStatusMapping } from "../../../../visualizations/visualizations";
import { createPortal } from "react-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { getRecordById } from "../../../../localDB";

const TauriCalendar = ({ date, items, holidays, currentDate, handleAdd, handleClick, worksheet, policies, handleRemove }) => {
    const longPressTimeout = useRef(null);
    const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const [longPressEvents, setLongPressEvents] = useState(null);
    const [longPressPosition, setLongPressPosition] = useState({ x: 0, y: 0 });

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const firstDayIndex = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
    const getItemDetails = (day) => {
        if (!Array.isArray(items)) return {};

        const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

        return items.find((item) => {
            const parsedItemDate = new Date(item.date); // Auto-detects both formats
            return (
                parsedItemDate.getFullYear() === targetDate.getFullYear() &&
                parsedItemDate.getMonth() === targetDate.getMonth() &&
                parsedItemDate.getDate() === targetDate.getDate()
            );
        }) || {};
    };

    const canEditWorksheet = editableWorksheetStatuses.includes(
        worksheet?.Status
    );

    // Make it async because we will await getRecordById
    const getHolidayDetails = (events) => {
        if (!events || !Array.isArray(events))
            return { isHoliday: false };

        // Find the first holiday event inside the list
        const holidayEvent = events.find(e => e.type === eventTypes.Holidays);

        if (holidayEvent) {
            try {
                const record = holidays.find(a => a.ID === holidayEvent.referenceID);
                return {
                    isHoliday: true,
                    name: record?.Description || ""
                };
            } catch (err) {
                return { isHoliday: true, name: "" };
            }
        }

        // No holiday event found
        return { isHoliday: false };
    };

    const validDaysSet = new Set(Object.keys(worksheet?.Days || {}));

    const renderTooltip = (props, text) => <Tooltip id="tooltip" {...props}>{text}</Tooltip>;

    const handleLongPressStart = (day, dayEvents, e) => {
        if(!canEditWorksheet) return;
        if (!dayEvents || dayEvents.length === 0) return;
        longPressTimeout.current = setTimeout(() => {
            const rect = e.target.getBoundingClientRect();

            setLongPressPosition({
                x: rect.left + window.scrollX,
                y: rect.bottom + window.scrollY
            });

            setLongPressEvents(dayEvents); // show ALL events
        }, 500);
    };

    const handleLongPressEnd = () => {
        clearTimeout(longPressTimeout.current);
    };

    useEffect(() => {
        const closeOnOutside = (e) => {
            const menu = document.getElementById("longpress_menu");

            // If menu exists and the click is outside → close menu
            if (menu && !menu.contains(e.target)) {
                setLongPressEvents(null);
            }
        };

        if (longPressEvents) {
            document.addEventListener("mousedown", closeOnOutside);
            document.addEventListener("touchstart", closeOnOutside);
        }

        return () => {
            document.removeEventListener("mousedown", closeOnOutside);
            document.removeEventListener("touchstart", closeOnOutside);
        };
    }, [longPressEvents]);

    useEffect(() => {
        setLongPressEvents(null);
    }, [worksheet])


    const renderLongPressMenu = () => {
        if (!longPressEvents) return null;

        return createPortal(
            <div className="calendar-container">
                <div
                    id="longpress_menu"
                    className="long-press-menu"
                    style={{
                        position: "absolute",
                        top: longPressPosition.y,
                        left: longPressPosition.x,
                        fontSize: "0.75rem",
                        background: "white",
                        padding: "10px",
                        borderRadius: "6px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                        zIndex: 2,
                        width: "auto"
                    }}
                >
                    {longPressEvents.map((ev, index) => (
                        <div
                            key={index}
                            className="long-press-item"
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "5px 0",
                                borderBottom: "1px solid #eee"
                            }}
                        >
                            <div>{ev.description}</div>

                            <TauriImage
                                localImage="/Images/delete-icon.svg"
                                alt="delete"
                                className="delete-icon ms-2"
                                onClick={() => handleRemove(ev)}
                            />
                        </div>
                    ))}
                </div>
            </div>,
            document.body
        );
    };


    const renderTooltipEvent = (tooltipProps, events) => (
        <Tooltip id="calendar-tooltip" {...tooltipProps}>
            {!events || events.length === 0 ? (
                "No activity"
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {events.map((e, i) => (
                        <div key={i} style={{ whiteSpace: "nowrap" }}>
                            {e.description}
                        </div>
                    ))}
                </div>
            )}
        </Tooltip>
    );

    const renderTooltipWorkWith = (tooltipProps, workwith) => {
        const NameLoader = ({ id }) => {
            const [name, setName] = React.useState("Loading...");

            React.useEffect(() => {
                const load = async () => {
                    const rec = await getRecordById("PlanningAccounts", id);
                    setName(rec?.Name || "Unknown");
                };
                load();
            }, [id]);

            return <div style={{ whiteSpace: "nowrap" }}>{name}</div>;
        };

        return (
            <Tooltip id="calendar-tooltip" {...tooltipProps}>
                {!workwith || workwith.length === 0 ? (
                    "No Work With"
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {workwith.map((id, i) => (
                            <NameLoader key={i} id={id} />
                        ))}
                    </div>
                )}
            </Tooltip>
        );
    };


    return (
        <div className="calendar-container">
            <div className="calendar-table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            {weekDays.map((day) => <th key={day}>{day.toUpperCase()}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: Math.ceil((firstDayIndex + daysInMonth) / 7) }, (_, rowIndex) => (
                            <tr key={rowIndex}>
                                {weekDays.map((_, colIndex) => {
                                    const dayNumber = rowIndex * 7 + colIndex - firstDayIndex + 1;
                                    if (dayNumber < 1 || dayNumber > daysInMonth) return <td key={colIndex}></td>;

                                    const fullDateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber).toLocaleDateString("en-CA");

                                    // Check if this day exists in worksheet.Days
                                    const isInWorksheet = validDaysSet.has(fullDateStr);

                                    if (!isInWorksheet) {
                                        return <td key={colIndex}></td>;
                                    }

                                    const dayData = worksheet?.Days?.[fullDateStr];

                                    const holidayDetails = getHolidayDetails(dayData?.events);

                                    const props = getItemDetails(dayNumber);
                                    const isHoliday = holidayDetails.isHoliday;
                                    const holidayName = holidayDetails?.name || "";

                                    const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Shanghai" }));
                                    const isToday =
                                        today.getFullYear() === currentDate.getFullYear() &&
                                        today.getMonth() === currentDate.getMonth() &&
                                        today.getDate() === dayNumber;

                                    const fullYear = new Date(date.getFullYear(), date.getMonth(), dayNumber).toLocaleDateString("en-CA");
                                    const isActive = date.toLocaleDateString("en-CA") === fullYear;


                                    const hasNoAccounts = dayData && Array.isArray(dayData.accounts) && dayData.accounts.length === 0;
                                    const accountCount = Array.isArray(dayData?.accounts) ? dayData.accounts.length : 0;

                                    const events = dayData?.events.filter(a => a.type !== eventTypes.Holidays && a.type !== eventTypes.Leave);
                                    const workWith = dayData?.workWith;
                                    const eventAndLeave = dayData?.events.filter(a => a.type !== eventTypes.Holidays);
                                    const leave = dayData?.events.find(a => a.type === eventTypes.Leave);


                                    return (
                                        <td
                                            key={colIndex}
                                            onClick={() => {
                                                handleClick(fullYear);
                                            }}
                                            onMouseDown={(e) => handleLongPressStart(fullYear, eventAndLeave, e)}
                                            onMouseUp={handleLongPressEnd}
                                            onTouchStart={(e) => handleLongPressStart(fullYear, eventAndLeave, e)}
                                            onTouchEnd={handleLongPressEnd}
                                            className={`
                                                ${isToday ? "current-date" : ""} 
                                                ${isActive ? "active-cell" : ""} 
                                                ${isHoliday ? "holiday-cell" : ""}
                                            `}
                                        >
                                            <div className={`calendar-cell ${hasNoAccounts ? "no-accounts" : ""}`}>
                                                <div className="display-top">
                                                    {isHoliday ? (
                                                        <OverlayTrigger placement="top" overlay={renderTooltip(props, holidayName)}>
                                                            <span className="display-number">{dayNumber}</span>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <span className="display-number">{dayNumber}</span>
                                                    )}
                                                    <div className="priority-icon">
                                                        {props.item?.filter(i => i.icon.priority).reverse().map((i, index) => (
                                                            <OverlayTrigger key={index} placement="top" overlay={renderTooltip(props, i.value)}>
                                                                <div className="ms-1 d-flex">
                                                                    <TauriImage
                                                                        localImage={i.icon.name}
                                                                        alt="priority-icon"
                                                                        className="calendar-icon"
                                                                        color={i.icon.color}
                                                                    />
                                                                    <div className="priority-value">{i.value}</div>
                                                                </div>
                                                            </OverlayTrigger>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="display-middle">
                                                    <div className="day-event">
                                                        {leave?.description}
                                                    </div>
                                                </div>

                                                {(accountCount == 0 && events.length == 0 && !leave && workWith.length == 0) &&
                                                    <TauriImage
                                                        localImage="/Images/flat-color-icons--plus-gray.svg"  // Replace with your actual image source
                                                        alt="icon"
                                                        className="add-icon"
                                                        onClick={() => handleAdd(fullYear)}
                                                    />
                                                }

                                                {(accountCount > 0 || events.length > 0 || workWith.length > 0) && (() => {
                                                    const dailyMinPolicy = policies?.find(p => p.name === "workplan.policies.daily_minimum");
                                                    const dailyMin = dailyMinPolicy?.value || 0;

                                                    return (
                                                        <div className="display-bottom">
                                                            <div className="d-flex align-items-center">
                                                                {dailyMin > 0 && (
                                                                    accountCount >= dailyMin ? (
                                                                        <TauriImage
                                                                            localImage="/Images/material-symbols--check.svg"
                                                                            alt="icon"
                                                                            className="calendar-icon"
                                                                            color="#069D33"
                                                                        />
                                                                    ) : (
                                                                        <TauriImage
                                                                            localImage="/Images/material-symbols--warning.svg"
                                                                            alt="icon"
                                                                            className="calendar-icon"
                                                                            color="#FD9B08"
                                                                        />
                                                                    )
                                                                )}
                                                                <div className="non-priority-value">
                                                                    {dailyMin > 0 ? `${accountCount}/${dailyMin}` : accountCount}
                                                                </div>
                                                            </div>

                                                            {workWith.length > 0 &&
                                                                <OverlayTrigger placement="top" overlay={renderTooltipWorkWith(props, workWith)}>
                                                                    <div className="d-flex align-items-center">
                                                                        <TauriImage
                                                                            localImage="/Images/mdi--users.svg"
                                                                            alt="icon"
                                                                            className="calendar-icon"
                                                                            color={"#7264CB"}
                                                                        />
                                                                        <div className="non-priority-value">
                                                                            {workWith.length}
                                                                        </div>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            }

                                                            {events.length > 0 &&
                                                                <OverlayTrigger placement="top" overlay={renderTooltipEvent(props, events)}>
                                                                    <div className="d-flex align-items-center">
                                                                        <TauriImage
                                                                            localImage="/Images/mdi--calendar-star.svg"
                                                                            alt="icon"
                                                                            className="calendar-icon"
                                                                            color={"#7264CB"}
                                                                        />
                                                                        <div className="non-priority-value">
                                                                            {events.length}
                                                                        </div>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            }
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {renderLongPressMenu()}
        </div>
    );
};

export default TauriCalendar;
